import * as Sentry from "@sentry/browser";
import { get as _get} from "lodash";

export const SERVICE_MONITORING = 'monitoring';
export const CATEGORY_ANALYTICS = 'analytics';

export function getSentryConfig(i18n) {
    const lang = i18n.locale;
    const labels = {
        [lang]: i18n.t('tekst Monitorowanie błędów Sentry (cookie consent)'),
    };

    return {
        category: CATEGORY_ANALYTICS,
        service: SERVICE_MONITORING,
        config: {
            label: labels[lang] || labels['en']
        },
        cookieTable: {
            body: [
                {
                    name: "sentry-sc",
                    domain: "Sentry",
                    description: lang === 'pl' ? "Plik cookie ustawiony przez Sentry do śledzenia sesji i monitorowania błędów w czasie rzeczywistym" :
                                 lang === 'ua' ? "Файл cookie, встановлений Sentry для відстеження сесії та моніторингу помилок у реальному часі" :
                                 "Cookie set by Sentry for session tracking and real-time error monitoring",
                    expiration: lang === 'pl' ? "Sesja" :
                                lang === 'ua' ? "Сесія" :
                                "Session"
                }
            ]
        },
        autoClear: {
            cookies: [
                { name: 'sentry-sc' },
                { name: /^_sentry_/ }
            ]
        }
    };
}

const STACK_OVERFLOW_MESSAGE = 'Maximum call stack size exceeded';

/**
 * Czy w stosie zdarzenia jest choć jedna ramka z pliku .js, który serwujemy.
 * Kod wstrzyknięty przez przeglądarkę nie pochodzi z żadnego pliku — jego ramki
 * silnik przypisuje do adresu dokumentu.
 */
function hasFrameFromScriptFile(event) {
    const values = event?.exception?.values ?? [];

    return values.some((value) => (value?.stacktrace?.frames ?? []).some(
        (frame) => typeof frame?.filename === 'string' && /\.m?js([?#]|$)/.test(frame.filename)
    ));
}

/**
 * Chrome na iOS i aplikacja Google wstrzykują na stronę własne, zminifikowane skrypty
 * (__gCrWeb: autouzupełnianie, menedżer haseł, tłumaczenie). Kiedy przepełnią stos,
 * wyjątek łapie nasz globalny onerror i leci do Sentry z ramkami przypisanymi do adresu
 * dokumentu — wygląda jak rekursja w panelu, a jest rekursją przeglądarki.
 *
 * Dowód (Sentry, 90 dni, message:"Maximum call stack size exceeded"): 270/270 zdarzeń
 * z Chrome Mobile iOS i aplikacji Google, zero z Mobile Safari — a to ten sam silnik
 * WebKit, ten sam limit stosu i te same strony. Naszego kodu w tym cyklu nie ma.
 *
 * Odrzucamy więc wyłącznie przepełnienie stosu bez ani jednej ramki z naszego pliku .js.
 * Gdyby stos przepełnił nasz kod, w ramkach będzie plik z bundla i zdarzenie przejdzie.
 */
function isStackOverflowOutsideOurScripts(event) {
    const isStackOverflow = (event?.exception?.values ?? []).some(
        (value) => typeof value?.value === 'string' && value.value.includes(STACK_OVERFLOW_MESSAGE)
    );

    return isStackOverflow && !hasFrameFromScriptFile(event);
}

/**
 * Widgety innych firm ładowane na nasze strony z własnym bundlem. Ich nieobsłużone błędy
 * (np. timeout żądań HelpScout Beacon do beaconapi.helpscout.net — SON-FRD) łapie nasz
 * globalny handler, choć naszego kodu w stosie nie ma.
 */
const THIRD_PARTY_SCRIPT_URL = /^https:\/\/beacon-v2\.helpscout\.net\//;

/**
 * Czy cały stos zdarzenia pochodzi ze skryptu widgetu innej firmy. Zdarzenie bez ramek
 * albo z choć jedną ramką spoza widgetu przechodzi.
 */
function isErrorFromThirdPartyScriptOnly(event) {
    const frames = (event?.exception?.values ?? []).flatMap((value) => value?.stacktrace?.frames ?? [])
        .filter((frame) => typeof frame?.filename === 'string');

    return frames.length > 0 && frames.every((frame) => THIRD_PARTY_SCRIPT_URL.test(frame.filename));
}

/**
 * Środowisko podaje backend metatagiem (layouts/common/formats.blade.php). Bez tego Sentry
 * domyślnie tagował KAŻDE zdarzenie jako "production" i błędy z maszyn deweloperskich
 * zalegały w produkcyjnej kolejce — widać to po trace'ach, w których event frontowy miał
 * environment: production, a backendowy z tego samego żądania environment: local.
 * Tryb budowania (import.meta.env.MODE) tu nie wystarczy: developer buduje assety tą samą
 * komendą co CI, więc też dostałby "production".
 */
function resolveEnvironment() {
    return document.querySelector('meta[name="app-environment"]')?.content || 'unknown';
}

export default function initializeSentry() {
    if (window.scriptsLoaded.sentry || !import.meta.env.VITE_SENTRY_DSN) return;

    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    const currentUser = typeof AuthUser !== 'undefined' ? AuthUser : {};
    const sentry_session_replay = _get(currentUser, 'instance.sentry_session_replay');
        Sentry.init({
            dsn: sentryDsn,
            environment: resolveEnvironment(),
            integrations: [
                Sentry.browserTracingIntegration(),
                ...(!!sentry_session_replay ? [Sentry.replayIntegration()] : [])
            ],
            tracesSampleRate: 0.2,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            beforeSend(event, hint) {
                const error = hint.originalException;
                if (error && error.response && error.response.headers) {
                    const ignoreSentryHeader = error.response.headers.get('X-Ignore-Sentry');
                    if (ignoreSentryHeader === 'true') {
                        return null;
                    }
                }

                const listOfErrorMessagesToIgnore = [
                    'Request failed with status code 401',
                    'Request failed with status code 403',
                    'Request failed with status code 404',
                    'Request failed with status code 419',
                    'Request failed with status code 422',
                    'Request failed with status code 503'
                ];

                // exception.value to string z komunikatem, nigdy obiekt — sprawdzenie
                // instanceof jQuery.Event miało tu sens zerowy. Zdarzenie jQuery trzeba
                // rozpoznać na surowym wyjątku z hint.originalException (S3-11591).
                if (error instanceof jQuery.Event) {
                    return null;
                }

                if (event.exception && event.exception.values) {
                    for (const exception of event.exception.values) {
                        if (exception.value && listOfErrorMessagesToIgnore.includes(exception.value)) {
                            return null;
                        }
                    }
                }

                if (isStackOverflowOutsideOurScripts(event)) {
                    return null;
                }

                if (isErrorFromThirdPartyScriptOnly(event)) {
                    return null;
                }

                return event;
            },
            ignoreErrors: [
                'NavigationDuplicated',
                /Navigation cancelled from/,
                /Redirected when going from/,
                /Navigation aborted from/,
                /Avoided redundant navigation/,
                'Network Error',
                'Request aborted',
                /^TypeError: Failed to fetch dynamically imported module/,
                /^SQLSTATE\[HY000\] \[2002\] Connection refused.*/,
                /^The check named `.*` did not complete.*/
            ]
        });
    window.scriptsLoaded.sentry = true;
}

export const cookieCallbacks = {
    onFirstConsent: (cookie, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    },

    onConsent: (cookie, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    },

    onChange: (cookie, changedCategories, changedServices, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    }
}
